
/**
 * LAS DOS PUERTAS DEL SITIO.
 *
 * Son las dos razones por las que alguien llega a un sitio de canalizaciones,
 * y Faina las nombró las dos (26-ago):
 *
 *   QUÉ TE ESTÁ PASANDO   →  porque le duele algo
 *   QUÉ TE GUSTARÍA SABER →  porque quiere saber algo
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 CÓMO ENCUENTRA CADA PUERTA SUS PIEZAS — TRES INTENTOS, Y POR QUÉ ESTE
 *
 * ① POR EL TÍTULO (hasta el 30-sep). Se buscaba la palabra de la puerta dentro
 *    del título. Funcionó mientras los títulos eran nombres de tema —«La
 *    culpa», «El duelo»—. Los 300 títulos nuevos del 28-sep los volvieron
 *    frases al lector («Vas por buen camino»), y el tema dejó de estar ahí:
 *    34 de 71 claves sin resultados, 2 puertas vacías, **19 % de cobertura**.
 *
 * ② POR EL CÓDIGO DE ENSEÑANZA solo. Cobertura 100 % y estable, pero los
 *    racimos son gruesos: D4 es «el cuerpo, la salud y la muerte», así que
 *    «Perdí a alguien» ofrecía «Tu cuerpo no es tu enemigo». Faina, 30-sep:
 *    «le despliega títulos que no tienen mucho que ver».
 *
 * ③ POR PALABRAS CLAVE solo. Precisa arriba —las cuatro primeras de «Perdí a
 *    alguien» eran exactas— pero **frágil**: la cola es arbitraria, la
 *    cobertura cae al 53 % y afinar una lista descuadra otra (quitar «solo»
 *    como palabra llevó «Estoy solo» de 78 piezas a 2).
 *
 * ⭐ LO QUE QUEDÓ: LAS DOS COSAS, CADA UNA EN SU OFICIO.
 *    · El **código** decide QUIÉN entra — curado, estable, no depende de cómo
 *      se llame la pieza. Se mira el EJE, el PRIMER código: el que dice qué
 *      enseña como espina, no el ámbito de vida.
 *    · Las **palabras** deciden EN QUÉ ORDEN, y **descartan lo que no menciona
 *      el tema ni una vez**. Por eso «Perdí a alguien» ya no muestra
 *      «Envejecer también tiene sentido».
 *    · Y una pieza de otro eje entra igual si habla mucho del tema (umbral
 *      alto), que es como vuelven las que el código se perdía.
 *
 * Resultado medido sobre 287: puertas de 3 a 44 piezas, **ninguna vacía**, y
 * todo lo que se ofrece menciona el tema. La cobertura baja a 78 %, y está
 * bien: **una puerta acompaña, no indexa** — el catálogo entero vive en la
 * Biblioteca.
 *
 * Crece solo: una pieza nueva entra por su código o por sus palabras.
 */


/** Cada puerta: los códigos que la componen y las palabras que la ordenan. */
export const ESTADOS = [
  { id: 'duelo', clave: 'duele.duelo', texto: 'Perdí a alguien', codigos: ['D4', 'F4'],
    claves: ['duelo', 'murio', 'muerte', 'morir', 'falleci', 'luto', 'despedir', 'despedida', 'ausencia'] },
  { id: 'culpa', clave: 'duele.culpa', texto: 'No me perdono', codigos: ['B6', 'F4'],
    claves: ['culpa', 'culpable', 'perdonarte', 'arrepent', 'remordimiento', 'verguenza'] },
  { id: 'valor', clave: 'duele.valor', texto: 'Siento que no valgo', codigos: ['B3', 'A4'],
    claves: ['vales', 'valer', 'merecimiento', 'merezco', 'mereces', 'merecer', 'indigno', 'indignidad', 'insuficiente', 'compararte'] },
  /* D1 (la Tierra como hogar) acompaña a C5 (somos uno): quien se siente solo
     necesita las dos, y así ese eje no se queda sin puerta. */
  { id: 'soledad', clave: 'duele.soledad', texto: 'Estoy solo', codigos: ['C5', 'E1', 'D1'],
    claves: ['soledad', 'solitari', 'aislad', 'incomprendid', 'nadie me entiende', 'no encajo', 'abandono'] },
  { id: 'miedo', clave: 'duele.miedo', texto: 'Tengo miedo', codigos: ['B1', 'D5'],
    claves: ['miedo', 'temor', 'panico', 'angustia', 'ansiedad', 'asusta', 'aterr'] },
  { id: 'estancado', clave: 'duele.estancado', texto: 'No avanzo', codigos: ['B5', 'F3'],
    claves: ['estancad', 'atascad', 'paciencia', 'no avanza', 'sin avanzar', 'retroced', 'impaciencia', 'recaid'] },
  { id: 'familia', clave: 'duele.familia', texto: 'Me duele mi familia', codigos: ['C3', 'B6'],
    claves: ['familia', 'padres', 'madre', 'padre', 'hijos', 'hermano', 'linaje', 'abuel', 'antepasad'] },
  /* 🧳 ALGUIEN QUE AMO ESTÁ LEJOS (Faina, 09-10-2026, D23). El vacío de casi
     todos los países: la familia partida por la emigración. El corpus no tiene
     cuerpos dedicados al tema (_filon: 0), así que NO se escribe pieza nueva:
     la puerta reúne, a mano y en este orden, las cuatro que ya lo tocan. Es la
     única puerta curada por ids; las demás se calculan solas. */
  { id: 'lejos', clave: 'duele.lejos', texto: 'Alguien que amo está lejos', codigos: [],
    ids: ['0017MA2107FA', '0021AN2107FA', '0085JE2107FA', '0020AN2107FA'],
    claves: ['lejos'] },
  { id: 'identidad', clave: 'duele.identidad', texto: 'No sé quién soy', codigos: ['A1', 'A3', 'A4'],
    claves: ['quien soy', 'quien eres', 'identidad', 'mascara', 'autentic', 'tu verdad', 'fingir'] },
  { id: 'amorpropio', clave: 'duele.amorpropio', texto: 'Me cuesta quererme', codigos: ['B3', 'A2'],
    claves: ['amor propio', 'amarte', 'quererte', 'quererme', 'duro contigo', 'te exiges', 'autoexigencia', 'te juzgas', 'autoestima', 'aceptarte'] },
  { id: 'dolor', clave: 'duele.dolor', texto: 'No puedo con el dolor', codigos: ['F4', 'B4', 'D5'],
    claves: ['dolor', 'tristeza', 'sufrimiento', 'angustia', 'noche oscura', 'desesperanza', 'llorar', 'llanto'] }
];

export const TEMAS = [
  { id: 'morir', clave: 'saber.morir', texto: 'Qué pasa cuando morimos', codigos: ['D4', 'E4'],
    claves: ['muerte', 'morir', 'mueres', 'mas alla', 'el otro lado', 'desencarn', 'vida despues'] },
  /* E2 y E3 (el parto de la nueva conciencia, los niños de la nueva era) tienen
     una pieza cada uno, y su sitio natural es la pregunta por la misión. */
  { id: 'para-que-vine', clave: 'saber.para_que_vine', texto: 'Para qué vine', codigos: ['E1', 'E4', 'C4', 'E2', 'E3'],
    claves: ['mision', 'proposito', 'viniste a', 'encarnacion', 'elegiste venir', 'trabajador de la luz'] },
  { id: 'quien-soy', clave: 'saber.quien_soy', texto: 'Quién soy en realidad', codigos: ['A1', 'A2', 'A3', 'C1'],
    claves: ['tu alma', 'eres divin', 'divinidad', 'yo soy', 'chispa', 'naturaleza divina', 'esencia'] },
  { id: 'dios', clave: 'saber.dios', texto: 'Cómo es Dios', codigos: ['F1', 'F2', 'E5'],
    claves: ['dios', 'la fuente', 'el padre', 'el creador', 'lo divino', 'castigador'] },
  { id: 'perdonar', clave: 'saber.perdonar', texto: 'Cómo se perdona', codigos: ['B6'],
    claves: ['perdon', 'perdonar', 'rencor', 'resentimiento', 'llama violeta'] },
  { id: 'amor', clave: 'saber.amor', texto: 'Qué es el amor', codigos: ['C2', 'C5', 'B3', 'D6'],
    claves: ['el amor', 'amar', 'amor incondicional', 'pareja', 'enamor'] },
  { id: 'voy-bien', clave: 'saber.voy_bien', texto: 'Cómo sé que voy bien', codigos: ['F5', 'F3', 'B5', 'A5'],
    claves: ['señal', 'señales', 'intuicion', 'confia', 'confianza', 'buen camino', 'duda', 'guia'] },
  { id: 'soltar', clave: 'saber.soltar', texto: 'Cómo se suelta', codigos: ['F3', 'D5', 'D2'],
    claves: ['soltar', 'suelta', 'dejar ir', 'entrega', 'rendirse', 'aferr', 'control', 'fluir'] },
  { id: 'dolor-sentido', clave: 'saber.dolor_sentido', texto: 'Para qué sirve el dolor', codigos: ['F4', 'B2'],
    claves: ['sufrimiento', 'aprendiste', 'maestro', 'gracia', 'crecer', 'la herida'] },
  { id: 'verdad', clave: 'saber.verdad', texto: 'Cómo hablo con la verdad', codigos: ['A3', 'C6'],
    claves: ['tu verdad', 'decir la verdad', 'callar', 'sincer', 'honest', 'coherencia', 'limites'] },
  { id: 'dinero', clave: 'saber.dinero', texto: 'Qué hago con el dinero', codigos: ['D3'],
    claves: ['dinero', 'abundancia', 'escasez', 'pobreza', 'deuda', 'riqueza', 'prosperidad', 'material', 'sustento'] }
];

/** Las dos listas juntas, para buscar una puerta por su id venga de donde venga. */
export const PUERTAS = [...ESTADOS, ...TEMAS];

export const slugsDePuerta = (doors, id) => {
  const puerta = PUERTAS.find((p) => p.id === id);
  const door = puerta && doors.find((d) => d.doorId === puerta.clave);
  return door ? door.slugs : [];
};

const conPiezas = (lista, doors) => lista
  .map((e) => ({ ...e, n: slugsDePuerta(doors, e.id).length }))
  .filter((e) => e.n > 0);

export const estadosConPiezas = (doors) => conPiezas(ESTADOS, doors);
export const temasConPiezas = (doors) => conPiezas(TEMAS, doors);
